import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { PlainTooltip } from "app/components/React/Tooltip/PlainTooltip/PlainTooltip";
import styled from "styled-components";
import { FlexColumn, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import type { ITableCellProps } from "app/components/React/Table/interfaces/ITableCellProps";
import { copySite, decodeSite, refreshTooltip } from "app/components/React/Table/Utils/urlCellsUtils";
import _ from "lodash";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";

const UrlWithTitleCellWrapper = styled(FlexColumn)``;

const UrlLinkContainer = styled.span``;

const TitleContainer = styled.div`
    width: 100%;
    margin: auto 0;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    overflow: hidden;
    ${setFontVariant("listItem1", colorsPalettes.carbon[400])};
`;

const UrlCellContainer = styled(FlexRow)`
    width: 100%;
    margin: auto 0;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    overflow: hidden;
    a {
        ${setFontVariant("listItem3", colorsPalettes.blue[400])};
        &:hover {
            ${setFontVariant("listItem3", colorsPalettes.blue[500])};
        }
    }
`;

const TooltipContentWrapper = styled(FlexColumn)`
    gap: 17px;
`;

const TooltipContentDiv = styled.div`
    overflow: hidden;
    overflow-wrap: break-word;
`;

export const UrlWithTitleCell: FC<Partial<ITableCellProps>> = ({ value, row }) => {
    const [isTitleTooltipEnabled, setIsTitleTooltipEnabled] = useState<boolean>(false);
    const [isUrlTooltipEnabled, setIsUrlTooltipEnabled] = useState<boolean>(false);
    const titleContainerRef = useRef(null);
    const urlContainerRef = useRef(null);

    useEffect(() => {
        return (
            refreshTooltip(titleContainerRef, setIsTitleTooltipEnabled) &&
            refreshTooltip(urlContainerRef, setIsUrlTooltipEnabled)
        );
    }, []);

    const siteDecoded = decodeSite(value);

    if (siteDecoded === "N/A" || !siteDecoded) {
        return (
            <UrlCellContainer>
                <UrlLinkContainer>-</UrlLinkContainer>
            </UrlCellContainer>
        );
    }

    const onCopy = (event: React.ClipboardEvent<HTMLDivElement>) => copySite(event, siteDecoded);

    const siteTitle = row.Title ? <TooltipContentDiv>{row.Title}</TooltipContentDiv> : null;

    const tooltipContent = (
        <TooltipContentWrapper>
            {siteTitle}
            <TooltipContentDiv>{siteDecoded}</TooltipContentDiv>
        </TooltipContentWrapper>
    );

    const url = _.startsWith(siteDecoded, "http") ? siteDecoded : `http://${siteDecoded}`;

    return (
        <UrlWithTitleCellWrapper>
            <PlainTooltip
                text={tooltipContent}
                enabled={isTitleTooltipEnabled || isUrlTooltipEnabled}
            >
                <div>
                    <TitleContainer ref={titleContainerRef}>{row.Title}</TitleContainer>
                    <UrlCellContainer
                        onCopy={onCopy}
                        ref={urlContainerRef}
                    >
                        <UrlLinkContainer className="url-cell-content">
                            <a
                                className="ad-target-url"
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {siteDecoded}
                            </a>
                        </UrlLinkContainer>
                    </UrlCellContainer>
                </div>
            </PlainTooltip>
        </UrlWithTitleCellWrapper>
    );
};
